import { createContext, Dispatch, SetStateAction, useContext, useState } from 'react'
import { OrderSheetCoupon } from '@shopby/shop-sdk'
import { ProductCoupon } from '../order/OrderProductSection'

type OrderContextType = {
  productCoupon: { [key: string]: ProductCoupon | null }
  setProductCoupon: (productNo: number, optionNo: number, coupon: ProductCoupon | null) => void
  cartCoupon?: OrderSheetCoupon | null
  setCartCoupon?: Dispatch<SetStateAction<OrderSheetCoupon | null>>
}

const OrderContext = createContext<OrderContextType>({
  productCoupon: {},
  setProductCoupon: () => {},
  cartCoupon: null,
  setCartCoupon: () => {},
})

const OrderProvider = ({ children }: { children: any }) => {
  const [productCoupon, setSelectedProductCoupon] = useState<{ [key: string]: ProductCoupon | null }>({})
  const [cartCoupon, setCartCoupon] = useState<OrderSheetCoupon | null>(null)

  const setProductCoupon = (productNo: number, optionNo: number, coupon: ProductCoupon | null) => {
    setSelectedProductCoupon((prev) => {
      const newState = { ...prev }
      const key = `${productNo}_${optionNo}`

      // 쿠폰 해제(명시적 null)
      if (coupon === null) {
        // 해당 productNo의 모든 옵션 쿠폰 해제
        Object.keys(newState).forEach((k) => {
          if (k.startsWith(`${productNo}_`)) {
            delete newState[k]
          }
        })
        return newState
      }

      // 동일 productNo의 모든 옵션 쿠폰 해제(새 쿠폰 적용 전)
      Object.keys(newState).forEach((k) => {
        if (k.startsWith(`${productNo}_`)) {
          delete newState[k]
        }
      })

      // 동일 쿠폰(쿠폰 1장만 적용 가능) 처리 (다른 product에도 중복 적용 방지)
      Object.entries(newState).forEach(([k, appliedCoupon]) => {
        if (
          appliedCoupon &&
          appliedCoupon.couponIssueNo === coupon.couponIssueNo &&
          appliedCoupon.couponNo === coupon.couponNo
        ) {
          delete newState[k]
        }
      })

      // 쿠폰 적용
      newState[key] = coupon
      return newState
    })
  }

  return (
    <OrderContext.Provider
      value={{
        productCoupon,
        setProductCoupon,
        cartCoupon,
        setCartCoupon,
      }}
    >
      {children}
    </OrderContext.Provider>
  )
}

const useOrderContext = () => {
  const context = useContext(OrderContext)
  if (!context) {
    throw new Error('useOrderContext 가 OrderProvider 안에서 사용되지 않았습니다.')
  }
  return context
}

export { OrderContext, OrderProvider, useOrderContext }
